"use client";
import { useEffect } from "react";

// Faerbt fuer die Lebensdauer der Seite den html-Hintergrund und das
// theme-color-Meta. Vollbild-Flow-Screens (z.B. Foto-Flow) bringen ihre
// eigene Flaechenfarbe mit — ohne diesen Hook zeigt iOS Safari hinter der
// Statusbar bzw. beim Overscroll unter der Toolbar das weisse html oder
// eine falsch getintete Leiste.
//
// Registry statt "vorherigen Wert merken": Beim App-Router kann die neue
// Seite mounten, BEVOR die alte unmountet. Ein Cleanup, der den beim
// eigenen Mount gemerkten Wert wiederherstellt, wuerde dann die Farbe der
// bereits gemounteten neuen Seite ueberschreiben — auf echten iOS-Geraeten
// blieb so bis zum Reload die Tönung der vorherigen Seite stehen. Hier
// gewinnt immer der zuletzt registrierte aktive Eintrag, unabhaengig von
// der Mount-/Unmount-Reihenfolge. Das Meta wird beim Seitenwechsel nur
// umgeschrieben (nicht entfernt und neu erstellt), damit Safari die
// Aenderung live uebernimmt; erst wenn keine Seite mehr eine Farbe
// deklariert, wird alles zurueckgebaut.
const activeEntries = [];

function applyTopEntry() {
  const html = document.documentElement;
  let meta = document.querySelector('meta[name="theme-color"]');
  const top = activeEntries.length
    ? activeEntries[activeEntries.length - 1]
    : null;

  if (top) {
    // Die echte Dokumentflaeche (html UND body) traegt die Farbe — iOS
    // Safari leitet seine Chrome-Toenung daraus ab; fixed-Overlays und
    // Meta-Aenderungen allein wertet es bei SPA-Wechseln nicht neu aus.
    html.style.backgroundColor = top.color;
    html.style.setProperty("--page-chrome-color", top.color);
    document.body.style.backgroundColor = top.color;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", top.color);
  } else {
    html.style.backgroundColor = "";
    html.style.removeProperty("--page-chrome-color");
    document.body.style.backgroundColor = "";
    if (meta) meta.remove();
  }
}

// Sofort-Anstrich innerhalb einer User-Geste (z.B. im onClick VOR dem
// router.push): iOS Safari uebernimmt Toenungswechsel zuverlaessig, wenn
// sie im Gesten-Kontext passieren — Aenderungen aus Effects nach dem
// Routenwechsel wertet es dagegen nicht immer neu aus. Der Ziel-Screen
// registriert sich danach regulaer ueber den Hook (idempotent).
export function applyChromeColorEagerly(color) {
  if (typeof document === "undefined" || !color) return;
  const html = document.documentElement;
  html.style.backgroundColor = color;
  html.style.setProperty("--page-chrome-color", color);
  document.body.style.backgroundColor = color;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", color);
}

// ---------------------------------------------------------------------------
// iOS Safari uebernimmt die Chrome-Toenung im Moment des Navigations-Events
// (pushState/popstate) aus dem DANN sichtbaren Dokumentzustand — spaetere
// Aenderungen (der Hook der Zielseite laeuft erst nach dem Routenwechsel)
// ignoriert es bis zum naechsten Event. Frame-Analyse: die Statusbar hing
// dadurch immer genau eine Seite hinterher. Darum wird die Farbe der
// Ziel-Route hier zentral VOR der Navigation angewendet: pushState/
// replaceState werden gepatcht, popstate (Back/Swipe) wird abgefangen.
// Gescoped auf den Foto-Flow; alle anderen Routen bleiben unberuehrt.
const FLOW_SEGMENT = "/fotos/fotos-schiessen";

const FLOW_ROUTE_COLORS = [
  [/\/fotos-schiessen\/karosserieschaeden/, "#1b1c1b"],
  [/\/fotos-schiessen\/keine-schaeden/, "#1b1c1b"],
  [/\/fotos-schiessen\/exterior-fertig/, "#1b1c1b"],
  [/\/fotos-schiessen\/schadensart-auswahl/, "#1b1c1b"],
  [/\/fotos-schiessen\/warnleuchten-check/, "#1b1c1b"],
  // Aussenschaeden-Overview mit open-Param startet direkt die Kamera
  [/\/fotos-schiessen\/aussenschaeden[^#]*[?&]open=/, "#1b1c1b"],
];

function chromeColorForUrl(url) {
  if (!url) return null;
  for (const [pattern, color] of FLOW_ROUTE_COLORS) {
    if (pattern.test(url)) return color;
  }
  // Der Händler-Foto-Flow verwendet durchgehend den dunklen Händler-Chrome.
  if (url.includes(FLOW_SEGMENT)) return "#1b1c1b";
  return null;
}

function handleFlowNavigation(targetUrl) {
  const from = window.location.href;
  const inFlow =
    (targetUrl && String(targetUrl).includes(FLOW_SEGMENT)) ||
    from.includes(FLOW_SEGMENT);
  if (!inFlow) return;
  const color =
    chromeColorForUrl(String(targetUrl ?? from)) ?? "#1b1c1b";
  applyChromeColorEagerly(color);
}

let navigationPatchInstalled = false;

// Einmalig aus einem immer gemounteten Ort installieren (AppLayout):
// der Patch muss auch greifen, wenn die AKTUELLE Seite ungetoent ist
// (kalter Einstieg Uebersicht -> exterior-fertig etc.) — der Scope-Check
// in handleFlowNavigation macht ihn ausserhalb des Flows zum No-Op.
export function installChromeColorNavigationPatch() {
  installNavigationPatch();
}

function installNavigationPatch() {
  if (navigationPatchInstalled || typeof window === "undefined") return;
  navigationPatchInstalled = true;

  for (const method of ["pushState", "replaceState"]) {
    const original = window.history[method].bind(window.history);
    window.history[method] = function patchedHistoryMethod(
      state,
      unused,
      url
    ) {
      try {
        if (url != null) {
          handleFlowNavigation(new URL(url, window.location.href).href);
        }
      } catch {
        // URL-Parsing darf eine Navigation nie verhindern.
      }
      return original(state, unused, url);
    };
  }

  window.addEventListener("popstate", () => {
    // Beim popstate ist location bereits die Ziel-URL.
    handleFlowNavigation(window.location.href);
  });
}

export default function usePageChromeColor(color) {
  useEffect(() => {
    if (!color) return undefined;

    installNavigationPatch();

    const entry = { color };
    activeEntries.push(entry);
    applyTopEntry();

    return () => {
      const index = activeEntries.indexOf(entry);
      if (index !== -1) activeEntries.splice(index, 1);
      applyTopEntry();
    };
  }, [color]);
}
